/**********************************************************************
// @@@ START COPYRIGHT @@@
// AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
// Copyright 2018 - 2019
// Avi Solar Energy Pvt Ltd.
// Protected as an unpublished work.
// The computer program listings, specifications and documentation
// herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
// and shall not be reproduced, copied, disclosed, or used in whole or in part for
// any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
<template>
  <div>
    <div class="legend-container">
      <MapLegend
        v-for="leg in legend"
        :key="leg.name"
        class="px-2 py-1"
        :text="$t(leg.name)"
        :shape="leg.attr"
      />
    </div>
    <div class="container-height">
      <div v-for="(block, index) in blockList" class="data-card ml-2 mr-2 mb-3" :key="block.text">
        <p class="block-name text-xs-center pt-1">{{ block.text }}</p>
        <v-divider></v-divider>
        <div>
          <div v-for="(data1, deviceId) in groupedByBlock[block.id]" :key="deviceId">
            <v-divider></v-divider>
            <p class="text-xs-center pt-1">{{ data1.name }}</p>
            <v-layout class="pl-2 pr-2 pb-2" row wrap>
              <v-flex v-for="(value, param) in data1" :key="param" xs2>
                <div v-if="param != 'name' && paramConfig[param] != undefined && paramConfig[param]['signal_type'] == 'status'" class="status-card" :class="value == paramConfig[param]['healthy_value'] ? 'status-on' : value == null ? 'no-status' : 'status-off'">
                  <div class="truncate pl-1 pr-1">{{ nameMap[deviceId][param] }}</div>
                </div>
                <div v-else-if="param != 'name'" class="status-card">
                  <div class="truncate pl-1 pr-1">{{ nameMap[deviceId][param] }} :</div>
                  <div v-if="value != null" class="value-color">{{ value }}</div>
                  <div v-else class="no-data">No Data</div>
                </div>
              </v-flex>
            </v-layout>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import relayApi from '../services/relay'
import { mapGettersMixin } from '@/util/mixin/index'
import MapLegend from '../components/common/MapLegend.vue'

export default {
  mixins: [ mapGettersMixin ],
  components: {
    MapLegend
  },
  data() {
    return {
      blockList: [],
      relayData: {},
      paramConfig: {},
      nameMap: {},
      legend: [
        {
          name: "On / Healthy / Okay",
          attr: {
            name: "square",
            color: "#3CB371",
          },
        },
        {
          name: "Off / Fault / Not Okay",
          attr: {
            name: "square",
            color: "#FF5252",
          },
        },
        {
          name: "No Communication",
          attr: {
            name: "square",
            color: "#A7A7A7",
          },
        }
      ],
    }
  },
  created() {
    this.initialStates()
  },
  computed: {
    ...mapGetters(['getSelectedPlantId', 'getGeneralState']),
    groupedByBlock() {
      const groupedObject = {}
      Object.keys(this.relayData).forEach(key => {
        const block = key.split('-')[0]
        if (!groupedObject[block]) {
          groupedObject[block] = {};
        }
        groupedObject[block][key] = this.relayData[key]
      })

      return groupedObject
    }
  },
  methods: {
    ...mapMutations([ 'SET_PROGRESS_BAR' ]),
    initialStates() {
      this.blockList = []
      this.relayData = {}
      this.paramConfig = {}
      this.nameMap = {}
      this.callAllApi()
    },
    async callAllApi() {
      await this.getBlockList()
      await this.setIntervalWithoutDelay(this.getRelayData)
    },
    async getRelayData() {
      try {
        this.SET_PROGRESS_BAR(true)
        const response = await relayApi.getRelayData(this.getSelectedPlantId)
        this.relayData = response.data.data
        this.paramConfig = response.data.config
        this.nameMap = response.data.name_map
      } catch (error) {
        this.SET_PROGRESS_BAR(false)
      } finally {
        this.SET_PROGRESS_BAR(false) 
      }
    },
    async getBlockList() {
      try {
        this.SET_PROGRESS_BAR(true)
        const response = await relayApi.getBlockList(this.getSelectedPlantId)
        this.blockList = response.data.message
      } catch(err) {
        this.SET_PROGRESS_BAR(false)
      } finally {
        this.SET_PROGRESS_BAR(false)
      }
    },
  },
  watch: {
    getSelectedPlantId() {
      this.initialStates()
    }
  }
}
</script>

<style scoped>
.legend-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.data-card {
  background-color: var(--v-primary-base);
  border-radius: 12px;
  box-shadow: 0px 5px 5px 0px #334079;
}

.block-name {
  font-weight: bolder;
  font-size: 18px;
}

.status-card {
  border-radius: 10px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--v-primary-darken1);
}

.status-on {
  border: 3px solid #3CB371;
}

.status-off {
  border: 3px solid #FF5252;
}

.no-status {
  border: 3px solid #9E9E9E;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-size: 14px;
}

.container-height {
  height: 70vh;
  overflow-y: auto;
}

.no-data {
  color: #ff5252;
  text-align: center;
}

.value-color {
  color: var(--v-number-base);
}
</style>